import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";
import { Badge } from "../ui/badge.jsx";
import { Progress } from "../ui/progress.jsx";
import { 
  TrendingUp, 
  TrendingDown, 
  Calendar,
  Clock,
  Users,
  Target,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  PieChart
} from 'lucide-react';
import { motion } from 'framer-motion';

export default function ProjectStats({ projects }) {
  // Calculate advanced statistics
  const calculateAdvancedStats = () => {
    if (!projects || projects.length === 0) {
      return {
        totalProjects: 0,
        completionRate: 0,
        averageProgress: 0,
        onTime: 0,
        delayed: 0,
        upcoming: 0,
        activeMemberCount: 0,
        statusDistribution: [],
        monthlyTrend: { current: 0, previous: 0, change: 0 }
      };
    }

    const total = projects.length;
    const completed = projects.filter(p => p.status === 'completed').length;
    const inProgress = projects.filter(p => p.status === 'in_progress').length;
    const planning = projects.filter(p => p.status === 'planning').length;
    const paused = projects.filter(p => p.status === 'paused').length;

    // Calculate projects status relative to dates
    const today = new Date();
    let onTime = 0;
    let delayed = 0;
    let upcoming = 0;

    projects.forEach(project => {
      if (project.end_date) {
        const endDate = new Date(project.end_date);
        if (project.status === 'completed') {
          onTime++;
        } else if (endDate < today) {
          delayed++;
        } else {
          upcoming++;
        }
      } else {
        upcoming++;
      }
    });

    // Calculate average progress
    const progressValues = projects.map(project => {
      if (project.status === 'completed') return 100;
      if (project.status === 'in_progress') return 60;
      if (project.status === 'planning') return 20;
      if (project.status === 'paused') return 40;
      return 0;
    });
    const averageProgress = progressValues.reduce((sum, val) => sum + val, 0) / total;

    // Count unique active members
    const uniqueMembers = new Set();
    projects.forEach(project => {
      if (project.team && Array.isArray(project.team)) {
        project.team.forEach(member => uniqueMembers.add(member));
      }
      if (project.manager) {
        uniqueMembers.add(project.manager);
      }
    });

    // Status distribution for charts
    const statusDistribution = [
      { status: 'planning', count: planning, label: 'Planejamento', color: '#8b5cf6' },
      { status: 'in_progress', count: inProgress, label: 'Em Andamento', color: '#3b82f6' },
      { status: 'paused', count: paused, label: 'Pausado', color: '#f59e0b' },
      { status: 'completed', count: completed, label: 'Concluído', color: '#10b981' }
    ];

    // Monthly trend (simplified)
    const currentMonth = today.getMonth();
    const currentMonthProjects = projects.filter(p => {
      const createdDate = new Date(p.created_at || p.start_date);
      return createdDate.getMonth() === currentMonth;
    }).length;

    const previousMonthProjects = projects.filter(p => {
      const createdDate = new Date(p.created_at || p.start_date);
      return createdDate.getMonth() === (currentMonth - 1 + 12) % 12;
    }).length;

    const monthlyChange = previousMonthProjects > 0 
      ? ((currentMonthProjects - previousMonthProjects) / previousMonthProjects * 100)
      : 0;

    return {
      totalProjects: total,
      completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
      averageProgress: Math.round(averageProgress),
      onTime,
      delayed,
      upcoming,
      activeMemberCount: uniqueMembers.size,
      statusDistribution,
      monthlyTrend: {
        current: currentMonthProjects,
        previous: previousMonthProjects,
        change: monthlyChange
      }
    };
  };

  const stats = calculateAdvancedStats();

  const statCards = [
    {
      title: "Taxa de Conclusão",
      value: `${stats.completionRate}%`,
      description: "Projetos finalizados",
      icon: Target,
      color: "text-green-600",
      bgColor: "bg-green-50",
      progress: stats.completionRate
    },
    {
      title: "Progresso Médio",
      value: `${stats.averageProgress}%`,
      description: "Progresso geral",
      icon: BarChart3,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      progress: stats.averageProgress
    },
    {
      title: "No Prazo",
      value: stats.onTime,
      description: "Projetos em dia",
      icon: CheckCircle2,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      title: "Atrasados",
      value: stats.delayed,
      description: "Necessitam atenção",
      icon: AlertTriangle,
      color: "text-red-600",
      bgColor: "bg-red-50"
    },
    {
      title: "Membros Ativos",
      value: stats.activeMemberCount,
      description: "Colaboradores envolvidos",
      icon: Users,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      title: "Tendência Mensal",
      value: stats.monthlyTrend.change > 0 ? `+${Math.round(stats.monthlyTrend.change)}%` : `${Math.round(stats.monthlyTrend.change)}%`,
      description: "vs mês anterior",
      icon: stats.monthlyTrend.change >= 0 ? TrendingUp : TrendingDown,
      color: stats.monthlyTrend.change >= 0 ? "text-green-600" : "text-red-600",
      bgColor: stats.monthlyTrend.change >= 0 ? "bg-green-50" : "bg-red-50"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className={`${stat.bgColor} border-none shadow-sm hover:shadow-md transition-all duration-200`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  {stat.progress && (
                    <span className={`text-xs font-medium ${stat.color}`}>
                      {stat.progress}%
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  <p className={`text-2xl font-bold ${stat.color}`}>
                    {stat.value}
                  </p>
                  <p className="text-xs text-muted-foreground font-medium">
                    {stat.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stat.description}
                  </p>
                </div>
                {stat.progress && (
                  <div className="mt-3">
                    <Progress 
                      value={stat.progress} 
                      className="h-1.5" 
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Status Distribution */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Distribuição por Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.statusDistribution.map((item, index) => (
                <div key={item.status} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">{item.count}</span>
                      <Badge variant="secondary" className="text-xs">
                        {stats.totalProjects > 0 ? Math.round((item.count / stats.totalProjects) * 100) : 0}%
                      </Badge>
                    </div>
                  </div>
                                     <Progress 
                     value={stats.totalProjects > 0 ? (item.count / stats.totalProjects) * 100 : 0}
                     className="h-2"
                   />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <Target className="w-5 h-5" />
              Insights Rápidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center gap-2 text-blue-800">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>
                  <strong>{stats.onTime}</strong> projetos no prazo
                </span>
              </div>
              <div className="flex items-center gap-2 text-blue-800">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span>
                  <strong>{stats.delayed}</strong> projetos atrasados
                </span>
              </div>
              <div className="flex items-center gap-2 text-blue-800">
                <Users className="w-4 h-4 text-purple-600" />
                <span>
                  <strong>{stats.activeMemberCount}</strong> membros ativos
                </span>
              </div>
              <div className="flex items-center gap-2 text-blue-800">
                <BarChart3 className="w-4 h-4 text-blue-600" />
                <span>
                  <strong>{stats.averageProgress}%</strong> progresso médio
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
} 